import { z } from 'zod';
import { UserRef } from '../ingest/identity.ts';

/**
 * Privacy requests (BB-39, DAT-014): export everything a profile holds, or delete it. Deleting erases the profile's
 * rows in every module (participants), detaches its devices and leaves a tombstone: the external ID cannot be revived
 * by late retries until an employee releases it. Aggregates without personal data stay.
 */
export const PRIVACY_KINDS = ['export', 'delete'] as const;
export const PRIVACY_STATUSES = ['accepted', 'running', 'completed', 'failed'] as const;
export type PrivacyKind = (typeof PRIVACY_KINDS)[number];
export type PrivacyStatus = (typeof PRIVACY_STATUSES)[number];
/** An export file can be downloaded this long after it is written; then it is deleted. */
export const PRIVACY_EXPORT_TTL_DAYS = 7;

const base = {
  /** Idempotency: the same id returns the same request. */
  requestId: z.uuid(),
  kind: z.enum(PRIVACY_KINDS),
  reason: z.string().trim().min(1).max(500).optional(),
};

/** `POST /server/v1/privacy/requests` (scope `privacy.jobs`). */
export const ServerPrivacyRequestBody = z.strictObject({ ...base, user: UserRef });
export type ServerPrivacyRequestBody = z.infer<typeof ServerPrivacyRequestBody>;

/** `POST /admin/v1/environments/:env/privacy/requests`: a user reference or a profile id. */
export const AdminPrivacyRequestBody = z
  .strictObject({ ...base, user: UserRef.optional(), profileId: z.uuid().optional() })
  .refine((b) => Boolean(b.user) !== Boolean(b.profileId), { message: 'give either user or profileId', path: ['user'] });
export type AdminPrivacyRequestBody = z.infer<typeof AdminPrivacyRequestBody>;

export const PrivacyListQuery = z.object({ cursor: z.uuid().optional(), limit: z.coerce.number().int().min(1).max(100).default(25) });
export const TombstoneQuery = z.object({ externalId: z.string().min(1).max(128) });
export const ReleaseTombstoneBody = z.strictObject({ reason: z.string().trim().min(1).max(500) });

export interface PrivacyStepView {
  participant: string;
  status: 'running' | 'completed' | 'failed';
  rowsAffected: number;
  completedAt: string | null;
  error: string | null;
}

export interface PrivacyRequestView {
  id: string;
  requestId: string;
  kind: PrivacyKind;
  status: PrivacyStatus;
  profileId: string;
  requestedBy: { type: 'server_key' | 'employee'; id: string; email: string | null };
  reason: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  error: string | null;
  steps: PrivacyStepView[];
  /** Exports only: where the file is while it lasts. */
  export: { available: boolean; expiresAt: string | null; downloadPath: string | null } | null;
}

export interface TombstoneView {
  id: string;
  profileId: string;
  requestId: string;
  deletedAt: string;
  recreatePolicy: 'block' | 'allow';
  releasedAt: string | null;
  releasedBy: { id: string; email: string | null } | null;
  releaseReason: string | null;
}

const Iso = z.iso.datetime();
/** The export file, version 1. Secrets were removed at ingest; push tokens appear only as hashes. */
export const PrivacyExport = z.object({
  privacyExportVersion: z.literal(1),
  environmentKey: z.string(),
  generatedAt: Iso,
  profile: z.object({
    profileId: z.uuid(),
    externalId: z.string().nullable(),
    createdAt: Iso,
    createdFrom: z.string(),
    aliases: z.array(z.object({ label: z.string(), name: z.string() })),
    standard: z.record(z.string(), z.unknown()),
    custom: z.record(z.string(), z.unknown()),
  }),
  installations: z.array(z.object({ installationId: z.uuid(), platform: z.string(), appVersion: z.string().nullable(), deviceModel: z.string().nullable(), firstSeenAt: Iso, lastSeenAt: Iso, pushTokenHashes: z.array(z.string()) })),
  appUsage: z.array(z.object({ appInstanceId: z.uuid(), firstUsedAt: Iso, lastUsedAt: Iso, sessionCount: z.number() })),
  sessions: z.array(z.object({ sessionId: z.uuid(), appInstanceId: z.uuid(), startedAt: Iso, endedAt: Iso.nullable() })),
  events: z.array(z.object({ name: z.string(), occurredAt: Iso, properties: z.record(z.string(), z.unknown()) })),
  /** AppsFlyer installs of the profile (BB-131). */
  installAttributions: z
    .array(z.object({ appsflyerId: z.string(), source: z.string().nullable(), campaign: z.string().nullable(), adgroup: z.string().nullable(), ad: z.string().nullable(), installAt: Iso.nullable(), receivedVia: z.string() }))
    .default([]),
  subscriptions: z.object({
    channels: z.array(z.object({ channel: z.string(), state: z.string(), changedAt: Iso })),
    groups: z.array(z.object({ groupId: z.uuid(), name: z.string(), state: z.string(), changedAt: Iso })),
    history: z.array(z.object({ scope: z.string(), target: z.string(), state: z.string(), changedAt: Iso, source: z.string() })),
  }),
});
export type PrivacyExport = z.infer<typeof PrivacyExport>;
